import { LEAD_SCORE_FILTER_LABEL } from "./engagement";
import { formatLeadSource, type LeadSourceLabels } from "./leadSource";

/**
 * THE CONTACTS LIST IS ASKED FOR, NOT DOWNLOADED.
 *
 * Until 2026-10-02 the Contacts page fetched the whole project into the browser
 * — 100 rows a request, 25 requests at most — and filtered, sorted, counted and
 * exported that array. Above 2,500 contacts it said so in a banner ("Filters,
 * totals and export cover the contacts shown"), which was honest and useless:
 * Black Oaks had 4,899, so every search, every stat card and every export on
 * that project silently left half of it out. And the shell's search panel
 * fetched the same 2,500 on EVERY screen of a project, 25 requests before the
 * reader had typed anything.
 *
 * Now the server answers the question the screen is asking — these filters,
 * this search, this order, rows N to N+60 — over the WHOLE project, with the
 * true count of matches, and the page draws what it was sent. A project of
 * 5,000 contacts or 50,000 costs the browser the same: one page.
 *
 * This file is the wire contract both sides read: the parameters, how they are
 * spelled in a URL, and the server's validation of them.
 */

/** Sort keys the list offers. Same set the page always had; ordered server-side now. */
export const CONTACT_LIST_SORTS = [
  "name",
  "email",
  "phone",
  "company",
  "location",
  "lifecycleStage",
  "engagementScore",
  "createdAt",
  "leadSource",
] as const;
export type ContactListSort = (typeof CONTACT_LIST_SORTS)[number];

/** Rows per page for a screen. */
export const CONTACT_LIST_PAGE = 60;
/** The most one request may ask for — the export reads in pages this size. */
export const CONTACT_LIST_MAX_LIMIT = 500;
/** `ids=` is for "recently viewed", which keeps 50. */
export const CONTACT_LIST_MAX_IDS = 50;

/**
 * The insight filters, AS THE SERVER READS THEM.
 *
 * The panel holds labels ("Hot (≥70)", "Yes", "Meta — Paid Social"); the wire
 * carries values the database can compare: `leadScore` is band names,
 * `leadSource` is the RAW stored values a chosen name stands for (one name can
 * be several ad URLs), `roles`/`buyerJourney` are lower-cased. Any other key is
 * a registration-form custom field and is matched against `custom_fields`.
 * The client builds this with `toServerFilters` in lib/contactList.ts.
 */
export type ContactListFilters = Record<string, string[]>;

export interface ContactListParams {
  projectId: string;
  /** Every word must appear in the name, an email, a phone or the company. */
  q?: string;
  /** "all" | "unassigned" | a user id. A Sales-role reader is pinned to themselves regardless. */
  owner?: string;
  /** "all" | a company id (SaleFish staff only, as before). */
  company?: string;
  filters?: ContactListFilters;
  sort?: ContactListSort;
  dir?: "asc" | "desc";
  offset?: number;
  limit?: number;
  /** Only these contacts, for the shell's "recently viewed". */
  ids?: string[];
}

export interface ContactListResponse<T = unknown> {
  contacts: T[];
  /** Contacts matching every filter, across the whole project — not this page. */
  total: number;
  /** Where the next page starts, or null when this was the last. */
  nextOffset: number | null;
}

/** The server's parsed reading of a request — see shared/contactListSchema.ts. */
export interface ContactListQuery {
  projectId: string;
  q?: string;
  owner?: string;
  company?: string;
  filters: ContactListFilters;
  sort: ContactListSort;
  dir?: "asc" | "desc";
  offset: number;
  limit: number;
  ids?: string[];
}

export interface ContactSummary {
  /** Every contact in the reader's scope (owner filter applied, nothing else). */
  total: number;
  qualified: number;
  hot: number;
  warm: number;
  cool: number;
}

/** Spell the parameters as a query string. Defaults are left out so equal views share a URL. */
export function contactListSearch(p: ContactListParams): string {
  const s = new URLSearchParams({ projectId: p.projectId });
  if (p.q?.trim()) s.set("q", p.q.trim());
  if (p.owner && p.owner !== "all") s.set("owner", p.owner);
  if (p.company && p.company !== "all") s.set("company", p.company);
  const filters = Object.fromEntries(
    Object.entries(p.filters ?? {}).filter(([, v]) => v.length > 0),
  );
  if (Object.keys(filters).length > 0) s.set("filters", JSON.stringify(filters));
  if (p.sort) s.set("sort", p.sort);
  if (p.dir) s.set("dir", p.dir);
  if (p.offset) s.set("offset", String(p.offset));
  if (p.limit) s.set("limit", String(p.limit));
  if (p.ids?.length) s.set("ids", p.ids.join(","));
  return s.toString();
}

/** Dates and scores read newest/highest first by default; text reads A→Z. */
export function defaultDirFor(sort: ContactListSort): "asc" | "desc" {
  return sort === "createdAt" || sort === "engagementScore" ? "desc" : "asc";
}

/**
 * The search words, folded the way the browser's `matchesAllWords` folds them:
 * lower case, accents dropped. The server folds the row the same way.
 */
export function searchWords(q: string | undefined): string[] {
  return (q ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 8);
}

/**
 * The panel's selections (labels) as the values the server compares.
 *
 * `leadSource` is the subtle one: the panel offers NAMES, and one name stands
 * for every raw value that formats to it — three Meta ad URLs are one "Meta —
 * Paid Social". So each chosen name becomes all the raw values the project
 * holds under it (`rawLeadSources` is the insights endpoint's list). The name
 * itself rides along too, which costs nothing and keeps a source whose raw
 * value IS its name findable before that list has loaded.
 */
export function toServerFilters(
  selected: Record<string, string[]>,
  rawLeadSources: string[],
  labels: LeadSourceLabels,
): ContactListFilters {
  const out: ContactListFilters = {};
  for (const [key, values] of Object.entries(selected)) {
    if (values.length === 0) continue;
    if (key === "leadScore") {
      out.leadScore = (Object.keys(LEAD_SCORE_FILTER_LABEL) as Array<keyof typeof LEAD_SCORE_FILTER_LABEL>).filter((band) =>
        values.includes(LEAD_SCORE_FILTER_LABEL[band]),
      );
    } else if (key === "leadSource") {
      const names = new Set(values);
      const raws = rawLeadSources.filter((raw) => names.has(formatLeadSource(raw, labels)));
      out.leadSource = [...new Set([...raws, ...values])];
    } else {
      out[key] = values;
    }
  }
  return out;
}
